/**
 * SocialOrganicPage — "Redes Orgánicas" (/social-organic).
 *
 * Instagram, Facebook y TikTok orgánicos (APIs oficiales, no scraping) en una
 * sola pantalla: una pestaña por red y un periodo compartido. Antes eran tres
 * tableros (/social-instagram, /social-facebook, /social-tiktok), que ahora
 * redirigen aquí con ?network=.
 *
 * Carga: las tres redes se piden EN PARALELO al entrar y cada respuesta se
 * guarda por (red, periodo). Cambiar de pestaña es instantáneo y volver a un
 * periodo ya visto no vuelve a llamar a Meta/TikTok. El periodo por defecto es
 * 30 días: coincide con el tope de insights de IG y TikTok, así que los KPIs
 * cubren exactamente el periodo, y con solo las publicaciones del periodo la
 * carga sigue siendo de pocos segundos.
 *
 * Lo propio de cada red vive en InstagramPanel / FacebookPanel / TiktokPanel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController,
  ArcElement, Tooltip, Legend,
} from 'chart.js';
import {
  FaInstagram, FaFacebookF, FaTiktok, FaSync, FaPlug, FaExclamationTriangle,
  FaRegCalendarAlt, FaFlask,
} from 'react-icons/fa';
import { useTranslation } from '../../i18n';
import { metaApi } from '../../services/metaService';
import { integrationsApi } from '../../services/integrationsService';
import {
  compact, resolveRange, longDate, setSocialLocale, ymd,
} from '../../components/SocialApi/socialApiShared';
import InstagramPanel from './InstagramPanel';
import FacebookPanel from './FacebookPanel';
import TiktokPanel from './TiktokPanel';
import '../../components/SocialApi/socialApi.css';
import './SocialOrganicPage.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, ArcElement, Tooltip, Legend);
ChartJS.defaults.font.family = "'Roboto', system-ui, -apple-system, sans-serif";
ChartJS.defaults.color = '#6b7a90';

const NETWORKS = [
  {
    id: 'instagram', name: 'Instagram', Icon: FaInstagram, Panel: InstagramPanel,
    fetch: (u, f, to) => metaApi.organicInstagram(u, f, to),
    handle: (d) => (d?.instagram?.username ? `@${d.instagram.username}` : null),
    followers: (d) => d?.instagram?.followers,
    configurePath: '/meta-config',
  },
  {
    id: 'facebook', name: 'Facebook', Icon: FaFacebookF, Panel: FacebookPanel,
    fetch: (u, f, to) => metaApi.organicFacebook(u, f, to),
    handle: (d) => d?.page?.name || null,
    followers: (d) => d?.page?.followers ?? d?.page?.fans,
    configurePath: '/meta-config',
  },
  {
    id: 'tiktok', name: 'TikTok', Icon: FaTiktok, Panel: TiktokPanel,
    fetch: (u, f, to) => integrationsApi.tiktokOrganicOverview(u, f, to),
    handle: (d) => (d?.account?.username ? `@${d.account.username}` : null),
    followers: (d) => d?.account?.followers,
    configurePath: '/admin?tab=integrations',
  },
];

const PRESETS = [
  { id: '7d', key: 'organic.last7', short: 'organic.short7' },
  { id: '30d', key: 'organic.last30', short: 'organic.short30' },
  { id: '90d', key: 'organic.last90', short: 'organic.short90' },
];

const NETWORK_STORAGE_KEY = 'organic_network';

export default function SocialOrganicPage({ userFeatures = {} }) {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  setSocialLocale(language);   // antes de pintar: números y fechas en el idioma de la app

  // El tenant del App Review de TikTok solo tiene TikTok: mostrarle pestañas de
  // Meta "sin conectar" confundiría al revisor.
  const networks = useMemo(
    () => (userFeatures.isTiktokReview ? NETWORKS.filter((n) => n.id === 'tiktok') : NETWORKS),
    [userFeatures.isTiktokReview]);

  // Red activa: ?network= manda (enlaces, redirecciones viejas); si no, la última usada.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('network');
  let remembered = null;
  try { remembered = localStorage.getItem(NETWORK_STORAGE_KEY); } catch (e) { /* sin storage */ }
  const activeId = [fromUrl, remembered].find((id) => networks.some((n) => n.id === id)) || networks[0].id;
  const active = networks.find((n) => n.id === activeId);
  const selectNetwork = (id) => {
    setSearchParams({ network: id }, { replace: true });
    try { localStorage.setItem(NETWORK_STORAGE_KEY, id); } catch (e) { /* sin storage */ }
  };

  const [range, setRange] = useState({ preset: '30d', from: '', to: '' });
  const { from, to } = resolveRange(range);
  const rangeReady = range.preset !== 'custom' || (from && to && from <= to);

  // Resultados por "red|desde|hasta": { status, data, error, at }. Guardarlos por
  // clave evita carreras: si llega tarde la respuesta de un periodo anterior, se
  // guarda en su casilla y no pisa lo que se está viendo.
  const [results, setResults] = useState({});
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const keyOf = useCallback((id) => `${id}|${from}|${to}`, [from, to]);

  const load = useCallback(async (net, force = false) => {
    if (!username || !rangeReady) return;
    const key = keyOf(net.id);
    const prev = resultsRef.current[key];
    if (!force && prev && prev.status !== 'error') return;
    setResults((r) => ({ ...r, [key]: { ...r[key], status: 'loading' } }));
    try {
      const data = await net.fetch(username, from, to);
      setResults((r) => ({ ...r, [key]: { status: 'ok', data, at: Date.now() } }));
    } catch (e) {
      // 400 = red no conectada / sin ID configurado; 404 = aún sin ruta/config.
      const status = e.status === 400 || e.status === 404 ? 'notConnected' : 'error';
      setResults((r) => ({ ...r, [key]: { ...r[key], status, error: e.message } }));
    }
  }, [username, rangeReady, keyOf, from, to]);

  useEffect(() => { networks.forEach((n) => load(n)); }, [networks, load]);

  const refresh = () => networks.forEach((n) => load(n, true));
  const current = results[keyOf(active.id)];
  const anyLoading = networks.some((n) => results[keyOf(n.id)]?.status === 'loading');

  // "Actualizado hace X min": se re-pinta cada 30 s.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const mins = current?.at ? Math.floor((Date.now() - current.at) / 60000) : null;

  const Panel = active.Panel;

  return (
    <main className="soc-page org-page">
      <header className="org-header">
        <div>
          <h1 className="org-title">{t('organic.title')}</h1>
          <p className="org-subtitle">{t('organic.subtitle')}</p>
        </div>
        <div className="org-header-actions">
          {mins != null && (
            <span className="org-updated">{mins < 1 ? t('organic.updatedJustNow') : t('organic.updatedMinutes', { n: mins })}</span>
          )}
          <button className="soc-btn" onClick={refresh} disabled={anyLoading || !rangeReady}>
            <FaSync className={anyLoading ? 'soc-spin' : ''} /> {t('organic.refresh')}
          </button>
        </div>
      </header>

      {networks.length > 1 && (
        <nav className="org-tabs" role="tablist">
          {networks.map((n) => {
            const r = results[keyOf(n.id)];
            const followers = r?.data ? n.followers(r.data) : null;
            let meta;
            if (r?.status === 'notConnected') meta = <span className="org-tab-muted">{t('organic.tabNotConnected')}</span>;
            else if (r?.status === 'error' && !r.data) meta = <span className="org-tab-muted">{t('organic.tabError')}</span>;
            else if (!r?.data) meta = <span className="org-tab-skel" aria-label={t('organic.tabLoading')} />;
            else {
              meta = (
                <>
                  {n.handle(r.data) && <span className="org-tab-handle">{n.handle(r.data)}</span>}
                  {followers != null && <span>{t('organic.followersShort', { n: compact(followers) })}</span>}
                </>
              );
            }
            return (
              <button key={n.id} type="button" role="tab" aria-selected={n.id === activeId}
                className={`org-tab net-${n.id} ${n.id === activeId ? 'active' : ''}`}
                onClick={() => selectNetwork(n.id)}>
                <span className="org-tab-icon"><n.Icon /></span>
                <span className="org-tab-text">
                  <span className="org-tab-name">{n.name}</span>
                  <span className="org-tab-meta">{meta}</span>
                </span>
              </button>
            );
          })}
        </nav>
      )}

      <PeriodBar t={t} range={range} onChange={setRange} from={from} to={to} ready={rangeReady} />

      {current?.data?.demo && (
        <div className="org-demo"><FaFlask /> {t('organic.demoNote')}</div>
      )}

      <section className={`org-panel ${current?.status === 'loading' && current?.data ? 'is-refreshing' : ''}`}>
        {!rangeReady ? (
          <div className="org-state"><FaRegCalendarAlt className="big" /><p>{t('organic.pickDates')}</p></div>
        ) : current?.status === 'notConnected' ? (
          <div className="org-state">
            <FaPlug className="big" />
            <h3>{t('organic.notConnectedTitle', { network: active.name })}</h3>
            <p>{t('organic.notConnectedBody')}</p>
            <button className="soc-btn primary" onClick={() => navigate(active.configurePath)}>{t('organic.goToIntegrations')}</button>
          </div>
        ) : current?.status === 'error' && !current.data ? (
          <div className="org-state">
            <FaExclamationTriangle className="big" />
            <h3>{t('organic.errorTitle')}</h3>
            <p title={current.error}>{t('organic.errorBody')}</p>
            <button className="soc-btn" onClick={() => load(active, true)}><FaSync /> {t('organic.retry')}</button>
          </div>
        ) : !current?.data ? (
          <PanelSkeleton />
        ) : (
          <Panel key={active.id} data={current.data} range={range} onRangeChange={setRange} username={username} t={t} />
        )}
      </section>
    </main>
  );
}

// ── Periodo: 7 / 30 / 90 días o fechas propias ───────────────────────────────
function PeriodBar({ t, range, onChange, from, to, ready }) {
  const custom = range.preset === 'custom';
  const today = ymd(new Date());   // fecha local, no UTC (en México de noche UTC ya es mañana)
  return (
    <div className="org-period">
      <span className="org-period-label"><FaRegCalendarAlt /> {t('organic.period')}</span>
      <div className="org-seg org-period-seg" role="group" aria-label={t('organic.period')}>
        {PRESETS.map((p) => (
          <button key={p.id} type="button" className={range.preset === p.id ? 'active' : ''}
            onClick={() => onChange({ preset: p.id, from: '', to: '' })}>
            {/* En celular va la etiqueta corta para que las 4 opciones quepan en un renglón. */}
            <span className="lbl-long">{t(p.key)}</span><span className="lbl-short">{t(p.short)}</span>
          </button>
        ))}
        <button type="button" className={custom ? 'active' : ''}
          onClick={() => onChange({ preset: 'custom', from: range.from || from || '', to: range.to || to || '' })}>
          <span className="lbl-long">{t('organic.custom')}</span><span className="lbl-short">{t('organic.customShort')}</span>
        </button>
      </div>
      {custom ? (
        <span className="org-dates">
          <label>{t('organic.from')}
            <input type="date" value={range.from || ''} max={range.to || today}
              onChange={(e) => onChange({ preset: 'custom', from: e.target.value, to: range.to || '' })} />
          </label>
          <label>{t('organic.to')}
            <input type="date" value={range.to || ''} min={range.from || undefined} max={today}
              onChange={(e) => onChange({ preset: 'custom', from: range.from || '', to: e.target.value })} />
          </label>
        </span>
      ) : ready && from && (
        <span className="org-period-range">{t('organic.showing', { from: longDate(from), to: longDate(to) })}</span>
      )}
    </div>
  );
}

// Esqueleto con la forma real del tablero: se lee "está cargando" sin el salto
// visual de un spinner que luego se convierte en tarjetas.
function PanelSkeleton() {
  return (
    <div className="org-skeleton" aria-busy="true">
      <div className="soc-kpis cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="org-skel-block kpi" />)}</div>
      <div className="org-skel-block summary" />
      <div className="soc-grid">
        <div className="org-skel-block chart col-8" />
        <div className="org-skel-block chart col-4" />
      </div>
    </div>
  );
}
